import { useState } from 'react';
import { AlertTriangle, Pin, Trash2 } from '../../../icons';
import { Button, FormField, Input, Modal, ModalActions, ModalFootNote, Select } from '../../../components/shared';
import {
  useDashboardStore, BOARD_KIND_LABEL, BOARD_VISIBILITY_LABEL, BOARD_COLORS,
  type BoardColor, type BoardKind, type BoardVisibility, type DashboardBoard,
} from '../../../store/dashboardStore';
import { getWidgetDef } from './widget-registry';

/**
 * 대시보드 설정 · 삭제 (2026-09-08 · 시안 화면 H · 2026-09-09 사용자 지시로 «한 곳»이 됐다).
 *
 * ★★★ **여기서 다 한다.** 종전에는 탭 ⋯ 메뉴에 「이름 변경」·「공유 범위」·「대시보드 설정」이
 *     따로 있었는데 **셋 다 이 창을 열 뿐**이었다(사용자 지적 2026-09-09). 메뉴는 한 줄로 줄이고,
 *     창이 시안 화면 H 가 그린 것을 전부 갖는다 — 이름 · **색** · 유형 · 공개 범위 ·
 *     **시작 화면** · 위젯 목록 · **삭제**.
 *
 * ★★★ 고르개는 **`portal` 로 띄운다.** 모달 본문이 `overflow:auto` 라 안에서 펼치면 잘리고
 *     **창에 스크롤만 생긴다**(사용자 지적 2026-09-09). 정본이 못 박아 둔 함정이고
 *     이 화면이 그것을 또 밟았다.
 *
 * ★★★ 삭제는 **무엇이 함께 사라지는지 먼저 보여 준다.** 시안이 그렇게 그렸고, 그래야 사람이
 *     되돌릴 수 없는 일을 하기 전에 멈출 수 있다 — 「위젯 자체는 없어지지 않는다. 이 대시보드에
 *     놓인 **배치**만 사라진다」까지 적는다(둘을 헷갈리면 지우기를 망설이거나 함부로 한다).
 */

const KINDS: BoardKind[] = ['personal', 'team', 'project'];
const VISIBILITIES: BoardVisibility[] = ['private', 'team', 'members'];

/** 공개 범위가 «누구에게» 보이는지 한 줄 — 시안의 「부서 공유면 … 12명이 봅니다」 자리. */
const VISIBILITY_HINT: Record<BoardVisibility, string> = {
  private: '나만 봅니다 — 탭도 나에게만 있습니다.',
  team: '같은 부서 사람들이 봅니다.',
  members: '지정한 사람들만 봅니다.',
};

export function BoardSettingsModal({
  board, onClose, onDelete,
}: {
  board: DashboardBoard;
  onClose: () => void;
  /** 창 안의 「대시보드 삭제」 — 삭제 확인 창으로 넘긴다(시안 화면 H 의 왼쪽 아래 단추). */
  onDelete?: (board: DashboardBoard) => void;
}) {
  const updateBoard = useDashboardStore((s) => s.updateBoard);
  const setDefaultBoard = useDashboardStore((s) => s.setDefaultBoard);
  const defaultBoardId = useDashboardStore((s) => s.defaultBoardId);

  const [name, setName] = useState(board.name);
  const [kind, setKind] = useState<BoardKind>(board.kind === 'corp' ? 'personal' : board.kind);
  const [visibility, setVisibility] = useState<BoardVisibility>(board.visibility);
  const [color, setColor] = useState<BoardColor | undefined>(board.color);
  const [isDefault, setIsDefault] = useState(defaultBoardId === board.id);

  const widgetNames = board.widgets.map((w) => getWidgetDef(w.widgetKey)?.label ?? w.widgetKey);

  const save = () => {
    updateBoard(board.id, { name: name.trim() || board.name, kind, visibility, ...(color ? { color } : {}) });
    // 시작 화면 — 켰으면 이 보드로, 껐는데 이 보드였으면 «마지막에 본 것»으로 되돌린다.
    if (isDefault) setDefaultBoard(board.id);
    else if (defaultBoardId === board.id) setDefaultBoard(null);
    onClose();
  };

  return (
    <Modal
      title="대시보드 설정" onClose={onClose} size="sm"
      footer={(
        <>
          {onDelete && !board.locked && (
            <ModalFootNote>
              <Button
                variant="quiet-danger" size="sm" iconLeft={<Trash2 size={14} />}
                onClick={() => { onClose(); onDelete(board); }}
              >
                대시보드 삭제
              </Button>
            </ModalFootNote>
          )}
          <ModalActions>
            <Button variant="secondary" onClick={onClose}>취소</Button>
            <Button variant="primary" onClick={save}>저장</Button>
          </ModalActions>
        </>
      )}
    >
      <FormField label="이름" required hint="탭에 이 이름이 보입니다.">
        <div className="dwc-namerow">
          {/* ★ 색은 «이름 옆»에 붙는다(시안) — 탭의 점이 이 색이 된다. */}
          <span className="dwc-swatches" role="radiogroup" aria-label="대시보드 색">
            {BOARD_COLORS.map((c) => (
              <button
                key={c.value}
                type="button" role="radio" aria-checked={color === c.value} aria-label={c.label}
                className={`dwc-swatch${color === c.value ? ' is-on' : ''}`}
                style={{ ['--c' as string]: c.token }}
                onClick={() => setColor(c.value)}
              />
            ))}
          </span>
          <Input value={name} maxLength={40} fullWidth onChange={(e) => setName(e.target.value)} />
        </div>
      </FormField>

      <div className="dwc-grid2">
        <FormField label="유형">
          {/* ⚠ `portal` 이 없으면 모달 본문(overflow:auto)에 잘려 **스크롤만 생긴다**. */}
          <Select
            ariaLabel="유형" value={kind} onChange={(v) => setKind(v as BoardKind)}
            options={KINDS.map((k) => ({ value: k, label: BOARD_KIND_LABEL[k] }))}
            portal
          />
        </FormField>

        <FormField label="공개 범위" hint={VISIBILITY_HINT[visibility]}>
          <Select
            ariaLabel="공개 범위" value={visibility} onChange={(v) => setVisibility(v as BoardVisibility)}
            options={VISIBILITIES.map((v) => ({ value: v, label: BOARD_VISIBILITY_LABEL[v] }))}
            portal
          />
        </FormField>
      </div>

      <FormField label="시작 화면" hint="로그인하면 어느 대시보드부터 볼지 정합니다.">
        <div className="dwc-segbar" role="radiogroup" aria-label="시작 화면">
          <button
            type="button" role="radio" aria-checked={isDefault}
            className={`dwc-segbar__i${isDefault ? ' is-active' : ''}`}
            onClick={() => setIsDefault(true)}
          >
            <Pin size={12} aria-hidden />로그인하면 이 대시보드로
          </button>
          <button
            type="button" role="radio" aria-checked={!isDefault}
            className={`dwc-segbar__i${isDefault ? '' : ' is-active'}`}
            onClick={() => setIsDefault(false)}
          >
            마지막에 본 것으로
          </button>
        </div>
      </FormField>

      <FormField label="위젯" hint={`${widgetNames.length}개 · 순서와 폭은 「레이아웃 편집」에서 바꿉니다.`}>
        {widgetNames.length === 0
          ? <p className="dwc-empty">아직 위젯이 없습니다.</p>
          : (
            <div className="dwx-losing">
              {widgetNames.map((n, i) => <span key={`${n}-${i}`}>{n}</span>)}
            </div>
          )}
      </FormField>
    </Modal>
  );
}

export function BoardDeleteModal({ board, onClose }: { board: DashboardBoard; onClose: () => void }) {
  const removeBoard = useDashboardStore((s) => s.removeBoard);
  const names = board.widgets.map((w) => getWidgetDef(w.widgetKey)?.label ?? w.widgetKey);

  return (
    <Modal
      title={`${board.name} 대시보드를 삭제할까요?`} onClose={onClose} size="sm"
      footer={(
        <>
          <ModalFootNote>되돌릴 수 없습니다</ModalFootNote>
          <ModalActions>
            <Button variant="secondary" onClick={onClose}>취소</Button>
            <Button variant="danger" iconLeft={<Trash2 size={14} />}
              onClick={() => { removeBoard(board.id); onClose(); }}>삭제</Button>
          </ModalActions>
        </>
      )}
    >
      <div className="dwx-danger">
        <AlertTriangle size={18} aria-hidden />
        <div>
          <b>이 대시보드와 그 배치가 사라집니다.</b> 되돌릴 수 없습니다.
        </div>
      </div>

      <FormField label="함께 사라지는 위젯 배치"
        hint="위젯 자체는 없어지지 않습니다 — 이 대시보드에 놓인 배치만 사라집니다.">
        <div className="dwx-losing">
          {names.length === 0 ? <span>놓인 위젯이 없습니다</span> : names.map((n, i) => <span key={`${n}-${i}`}>{n}</span>)}
        </div>
      </FormField>
    </Modal>
  );
}
